import { create } from "zustand";

import type { Inventory, UpsertInventoryValueInput } from "@/graphql/generated/graphql";
import { queryKeys } from "@/services/react-query.service/query-keys";
import type { QueryClient } from "@tanstack/react-query";

import type { Command, InsertRowsCommandPayload } from "./types";
import { DeleteRowsCommand } from "./types";

interface PendingTempDelete {
  tempId: string;
  inventoryId: string;
  rowData: UpsertInventoryValueInput;
}

interface CommandQueueState {
  pendingCommands: Command[];
  isSyncing: boolean;
  pendingTempDeletes: PendingTempDelete[];
  addCommand: (command: Command) => void;
  clearPending: () => void;
  setSyncing: (isSyncing: boolean) => void;
  getCommandsByInventory: (inventoryId: string) => Command[];
  removeCommandsByInventory: (inventoryId: string) => void;
  cancelRowFromInsertCommands: (inventoryId: string, rowData: UpsertInventoryValueInput) => void;
  cancelRowByTempId: (inventoryId: string, tempId: string) => boolean;
  addPendingTempDelete: (tempId: string, inventoryId: string, rowData: UpsertInventoryValueInput) => void;
  processPendingTempDeletes: (queryClient: QueryClient, inventoryId: string, deletedIds?: string[]) => void;
  processPendingTempDeletesWithIdMap: (inventoryId: string, idMap: Record<string, string>) => void;
}

export const useCommandQueueStore = create<CommandQueueState>((set, get) => ({
  pendingCommands: [],
  isSyncing: false,
  pendingTempDeletes: [],
  addCommand: (command: Command) =>
    set((state) => ({
      pendingCommands: [...state.pendingCommands, command],
    })),
  clearPending: () =>
    set(() => ({
      pendingCommands: [],
    })),
  setSyncing: (isSyncing: boolean) =>
    set(() => ({
      isSyncing,
    })),
  getCommandsByInventory: (inventoryId: string) => {
    return get().pendingCommands.filter((cmd) => cmd.inventoryId === inventoryId);
  },
  removeCommandsByInventory: (inventoryId: string) =>
    set((state) => ({
      pendingCommands: state.pendingCommands.filter((cmd) => cmd.inventoryId !== inventoryId),
    })),
  cancelRowFromInsertCommands: (inventoryId: string, rowData: UpsertInventoryValueInput) =>
    set((state) => {
      const updatedCommands = state.pendingCommands.map((cmd) => {
        if (cmd.type === "INSERT_ROWS" && cmd.inventoryId === inventoryId) {
          const payload = cmd.payload as InsertRowsCommandPayload;
          // Filter out rows that match the deleted row data
          const filteredRows = payload.rows.filter((row) => {
            // Match by key fields (excluding amount which might have slight differences)
            return !(
              row.energySourceId === rowData.energySourceId &&
              row.subsectorId === rowData.subsectorId &&
              row.sectorId === rowData.sectorId &&
              row.sectorSliceId === rowData.sectorSliceId &&
              row.subsubsectorId === rowData.subsubsectorId &&
              row.type === rowData.type
            );
          });
          // If all rows were filtered out, remove the command
          if (filteredRows.length === 0) {
            return null;
          }
          // Rebuild mappings to match filteredRows
          // Track which original indices were kept and their new positions
          const keptIndices: number[] = [];
          payload.rows.forEach((row, originalIndex) => {
            const shouldKeep = !(
              row.energySourceId === rowData.energySourceId &&
              row.subsectorId === rowData.subsectorId &&
              row.sectorId === rowData.sectorId &&
              row.sectorSliceId === rowData.sectorSliceId &&
              row.subsubsectorId === rowData.subsubsectorId &&
              row.type === rowData.type
            );
            if (shouldKeep) {
              keptIndices.push(originalIndex);
            }
          });
          // Rebuild mappings based on kept indices
          const updatedTempIdMap: Record<string, number> = {};
          const updatedRowToTempId = new Map<number, string>();
          keptIndices.forEach((originalIndex, newIndex) => {
            // Find temp ID for this original index
            const tempId = payload.rowToTempId?.get(originalIndex);
            if (tempId) {
              updatedTempIdMap[tempId] = newIndex;
              updatedRowToTempId.set(newIndex, tempId);
            }
          });
          // Return updated command with filtered rows and aligned mappings
          return {
            ...cmd,
            payload: {
              rows: filteredRows,
              tempIdToRowIndex: Object.keys(updatedTempIdMap).length > 0 ? updatedTempIdMap : undefined,
              rowToTempId: updatedRowToTempId.size > 0 ? updatedRowToTempId : undefined,
            } as InsertRowsCommandPayload,
          };
        }
        return cmd;
      });
      // Remove null commands (empty INSERT commands)
      return {
        pendingCommands: updatedCommands.filter((cmd): cmd is Command => cmd !== null),
      };
    }),
  cancelRowByTempId: (inventoryId: string, tempId: string) => {
    const state = useCommandQueueStore.getState();
    let found = false;
    const updatedCommands = state.pendingCommands.map((cmd) => {
      if (cmd.type === "INSERT_ROWS" && cmd.inventoryId === inventoryId) {
        const payload = cmd.payload as InsertRowsCommandPayload;
        const tempIdMap = payload.tempIdToRowIndex || {};
        const rowIndex = tempIdMap[tempId];

        if (rowIndex !== undefined) {
          found = true;
          // Remove the row at this index
          const filteredRows = payload.rows.filter((_, index) => index !== rowIndex);
          // Update the temp-id map to remove the deleted entry and adjust indices
          const updatedTempIdMap: Record<string, number> = {};
          Object.entries(tempIdMap).forEach(([tid, idx]) => {
            if (tid !== tempId) {
              // Adjust indices for rows after the deleted one
              updatedTempIdMap[tid] = idx > rowIndex ? idx - 1 : idx;
            }
          });

          // Rebuild rowToTempId mapping to match filteredRows and updatedTempIdMap
          const updatedRowToTempId = new Map<number, string>();
          Object.entries(updatedTempIdMap).forEach(([tid, newIndex]) => {
            updatedRowToTempId.set(newIndex, tid);
          });

          // If all rows were filtered out, remove the command
          if (filteredRows.length === 0) {
            return null;
          }
          // Return updated command with filtered rows and aligned mappings
          return {
            ...cmd,
            payload: {
              rows: filteredRows,
              tempIdToRowIndex: updatedTempIdMap,
              rowToTempId: updatedRowToTempId,
            } as InsertRowsCommandPayload,
          };
        }
      }
      return cmd;
    });
    // Remove null commands (empty INSERT commands)
    const filteredCommands = updatedCommands.filter((cmd): cmd is Command => cmd !== null);
    useCommandQueueStore.setState({ pendingCommands: filteredCommands });
    return found;
  },
  addPendingTempDelete: (tempId: string, inventoryId: string, rowData: UpsertInventoryValueInput) =>
    set((state) => {
      const newPending = [...state.pendingTempDeletes, { tempId, inventoryId, rowData }];
      return { pendingTempDeletes: newPending };
    }),
  processPendingTempDeletes: (queryClient: QueryClient, inventoryId: string, deletedIds?: string[]) => {
    const state = useCommandQueueStore.getState();
    const inventory = queryClient.getQueryData<Inventory>(queryKeys.inventories.detail(inventoryId));
    if (!inventory) {
      return;
    }

    const processed: PendingTempDelete[] = [];
    const remaining: PendingTempDelete[] = [];

    state.pendingTempDeletes.forEach((pending) => {
      if (pending.inventoryId !== inventoryId) {
        remaining.push(pending);
        return;
      }

      // Try to find the real-id by matching row data
      // Normalize undefined/null for comparison
      const normalize = (val: string | null | undefined) => val ?? null;

      const matchedValue = inventory.inventoryValueList?.find((iv) => {
        const energySourceMatch = normalize(iv.energySourceId) === normalize(pending.rowData.energySourceId);
        const subsectorMatch = normalize(iv.subsectorId) === normalize(pending.rowData.subsectorId);
        const sectorMatch = iv.sectorId === pending.rowData.sectorId;
        const sectorSliceMatch = normalize(iv.sectorSliceId) === normalize(pending.rowData.sectorSliceId);
        const subsubsectorMatch = normalize(iv.subsubsectorId) === normalize(pending.rowData.subsubsectorId);
        const typeMatch = iv.type === pending.rowData.type;
        const amountMatch = Math.abs((iv.amount || 0) - (pending.rowData.amount || 0)) < 0.0001;

        const matches =
          energySourceMatch &&
          subsectorMatch &&
          sectorMatch &&
          sectorSliceMatch &&
          subsubsectorMatch &&
          typeMatch &&
          amountMatch;

        return matches;
      });

      if (matchedValue && !matchedValue.id.startsWith("temp-")) {
        // Found real-id, create DELETE command
        const deleteCommand = new DeleteRowsCommand({ rowIds: [matchedValue.id] }, inventoryId);
        useCommandQueueStore.getState().addCommand(deleteCommand);
        processed.push(pending);
      } else if (matchedValue) {
        remaining.push(pending);
      } else {
        // Value not found - might have been deleted already
        // Check if it was in the deletedIds from WebSocket
        const wasDeleted = deletedIds?.includes(pending.tempId);
        if (!wasDeleted) {
          // Keep in remaining for next WebSocket update in case it appears later
          remaining.push(pending);
        }
      }
    });

    if (processed.length > 0) {
      useCommandQueueStore.setState({ pendingTempDeletes: remaining });
    }
  },
  processPendingTempDeletesWithIdMap: (inventoryId: string, idMap: Record<string, string>) => {
    const state = useCommandQueueStore.getState();
    const processed: PendingTempDelete[] = [];
    const remaining: PendingTempDelete[] = [];

    state.pendingTempDeletes.forEach((pending) => {
      if (pending.inventoryId !== inventoryId) {
        remaining.push(pending);
        return;
      }

      // Use idMap to find the real-id for this temp-id
      const realId = idMap[pending.tempId];

      if (realId) {
        // SUCCESS: Found the real-id in idMap
        const deleteCommand = new DeleteRowsCommand({ rowIds: [realId] }, inventoryId);
        useCommandQueueStore.getState().addCommand(deleteCommand);
        processed.push(pending);
      } else {
        // Not found in idMap - might be in a future update or already deleted
        remaining.push(pending);
      }
    });

    if (processed.length > 0) {
      useCommandQueueStore.setState({ pendingTempDeletes: remaining });
    }
  },
}));
